import { 
  getFirestore, 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import { auth } from './auth';
import { RedeemedReward } from '../types/reward';
import type { Reward, RewardHistoryData } from '../types/reward';
import { COLLECTIONS } from '../constants';
import { getErrorMessage } from '../utils/errorHandling';

const db = getFirestore();

// Helper to get current user ID
const getCurrentUserId = () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Please sign in to continue. You need to be authenticated to access your rewards.');
  }
  return user.uid;
};

export const rewardService = {
  // Redeem a reward
  async redeemReward(rewardId: string, rewardName: string, rewardDescription: string, cost: number, category: string): Promise<void> {
    try {
      const userId = getCurrentUserId();
      
      // Check if user has enough credits
      const userStatsQuery = query(
        collection(db, COLLECTIONS.USER_STATS),
        where('userId', '==', userId)
      );
      const userStatsSnapshot = await getDocs(userStatsQuery);
      
      if (userStatsSnapshot.empty) {
        throw new Error('User stats not found.');
      }
      
      const userStats = userStatsSnapshot.docs[0].data();
      const availableCredits = userStats.availableCredits || 0;
      
      if (availableCredits < cost) {
        throw new Error('Insufficient credits to redeem this reward.');
      }
      
      // Deduct credits from user stats
      const userStatsRef = userStatsSnapshot.docs[0].ref;
      await updateDoc(userStatsRef, {
        availableCredits: availableCredits - cost,
        updatedAt: serverTimestamp()
      });
      
      const redeemedReward: Omit<RedeemedReward, 'id'> = {
        userId,
        rewardId,
        rewardName,
        rewardDescription,
        cost,
        category,
        redeemedAt: new Date(),
        status: 'redeemed'
      };

      await addDoc(collection(db, COLLECTIONS.REDEEMED_REWARDS), {
        ...redeemedReward,
        redeemedAt: serverTimestamp()
      });

    } catch (error) {
      console.error('Error redeeming reward:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Request approval to use a reward
  async requestRewardUsageApproval(redeemedRewardId: string, notes?: string): Promise<void> {
    try {
      const userId = getCurrentUserId();
      
      // Check if user has approved credit approvers
      const userService = await import('./userService');
      const hasAcceptedCreditApprover = await userService.userService.hasAcceptedCreditApprover();
      
      if (!hasAcceptedCreditApprover) {
        throw new Error('You need approved credit approvers to use rewards');
      }

      // Get the reward document and update status to pending approval
      const rewardRef = doc(db, COLLECTIONS.REDEEMED_REWARDS, redeemedRewardId);
      
      await updateDoc(rewardRef, {
        status: 'pending_approval',
        approvalRequestedAt: serverTimestamp(),
        approvalNotes: notes || '',
        approvalRequestedBy: userId
      });

    } catch (error) {
      console.error('Error requesting reward usage approval:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Approve reward usage (for credit approvers)
  async approveRewardUsage(redeemedRewardId: string, notes?: string): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Please sign in to continue.');

      // Get the redeemed reward to check the user
      const redeemedRewardDoc = await getDoc(doc(db, COLLECTIONS.REDEEMED_REWARDS, redeemedRewardId));
      if (!redeemedRewardDoc.exists()) {
        throw new Error('Redeemed reward not found.');
      }

      const redeemedRewardData = redeemedRewardDoc.data();
      const rewardUserId = redeemedRewardData.userId;

      // Check if current user is an approver for this user
      const approverQuery = query(
        collection(db, COLLECTIONS.CREDIT_APPROVERS),
        where('userId', '==', rewardUserId),
        where('approverUid', '==', user.uid),
        where('status', '==', 'accepted')
      );
      const approverSnapshot = await getDocs(approverQuery);
      
      if (approverSnapshot.empty) {
        throw new Error('You are not authorized to approve rewards for this user.');
      }

      // Update the redeemed reward
      await updateDoc(doc(db, COLLECTIONS.REDEEMED_REWARDS, redeemedRewardId), {
        status: 'used',
        usedAt: serverTimestamp(),
        approvedBy: user.uid,
        approvedAt: serverTimestamp(),
        approverNotes: notes || ''
      });

      console.log('Reward usage approved:', redeemedRewardId);
    } catch (error) {
      console.error('Error approving reward usage:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Reject a reward usage request (called by approver)
  async rejectRewardUsage(redeemedRewardId: string, approverId: string, rejectionReason?: string): Promise<void> {
    try {
      const rewardRef = doc(db, COLLECTIONS.REDEEMED_REWARDS, redeemedRewardId);
      
      await updateDoc(rewardRef, {
        status: 'redeemed', // Back to redeemed status
        rejectedBy: approverId,
        rejectedAt: serverTimestamp(),
        rejectionReason: rejectionReason || ''
      });

    } catch (error) {
      console.error('Error rejecting reward usage:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Get pending approval requests for approvers
  async getPendingApprovalRequests(): Promise<RedeemedReward[]> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Please sign in to continue.');
      }

      // Get all users that the current user approves for
      const userService = await import('./userService');
      const usersIApprove = await userService.userService.getUsersIApprove();
      
      if (usersIApprove.length === 0) {
        return [];
      }

      // Get all pending approval requests from users that the current user approves for
      const pendingRequests: RedeemedReward[] = [];
      
      for (const userInfo of usersIApprove) {
        const q = query(
          collection(db, COLLECTIONS.REDEEMED_REWARDS),
          where('status', '==', 'pending_approval'),
          where('userId', '==', userInfo.userId)
        );
        
        const querySnapshot = await getDocs(q);
        
        const requests = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            userId: data.userId,
            rewardId: data.rewardId,
            rewardName: data.rewardName,
            rewardDescription: data.rewardDescription,
            cost: data.cost,
            category: data.category,
            status: data.status,
            redeemedAt: data.redeemedAt?.toDate() || new Date(),
            usedAt: data.usedAt?.toDate() || undefined,
            approvalRequestedAt: data.approvalRequestedAt?.toDate() || undefined,
            approvalNotes: data.approvalNotes,
            approvalRequestedBy: data.approvalRequestedBy,
            approvedBy: data.approvedBy,
            approvedAt: data.approvedAt?.toDate() || undefined,
            approverNotes: data.approverNotes,
            rejectedBy: data.rejectedBy,
            rejectedAt: data.rejectedAt?.toDate() || undefined,
            rejectionReason: data.rejectionReason,
            notes: data.notes
          } as RedeemedReward;
        });
        
        pendingRequests.push(...requests);
      }

      return pendingRequests;

    } catch (error) {
      console.error('Error getting pending approval requests:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Mark a reward as used (requires approval)
  async useReward(redeemedRewardId: string, notes?: string): Promise<void> {
    try {
      const userId = getCurrentUserId();
      
      // Request approval instead of directly using
      await this.requestRewardUsageApproval(redeemedRewardId, notes);

    } catch (error) {
      console.error('Error using reward:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Get user's reward history
  async getRewardHistory(): Promise<RewardHistoryData> {
    try {
      const userId = getCurrentUserId();
      
      const q = query(
        collection(db, COLLECTIONS.REDEEMED_REWARDS),
        where('userId', '==', userId),
        orderBy('redeemedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      
      const rewards = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          redeemedAt: data.redeemedAt?.toDate() || new Date(),
          usedAt: data.usedAt?.toDate() || undefined
        };
      }) as RedeemedReward[];

      const totalRedeemed = rewards.length;
      const totalUsed = rewards.filter(r => r.status === 'used').length;
      const totalSpent = rewards.reduce((sum, reward) => sum + reward.cost, 0);

      return {
        totalRedeemed,
        totalUsed,
        totalSpent,
        rewards
      };

    } catch (error) {
      console.error('Error getting reward history:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Get redeemed rewards by status
  async getRedeemedRewardsByStatus(status: 'redeemed' | 'used' | 'expired'): Promise<RedeemedReward[]> {
    try {
      const userId = getCurrentUserId();
      
      const q = query(
        collection(db, COLLECTIONS.REDEEMED_REWARDS),
        where('userId', '==', userId),
        where('status', '==', status),
        orderBy('redeemedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          redeemedAt: data.redeemedAt?.toDate() || new Date(),
          usedAt: data.usedAt?.toDate() || undefined
        };
      }) as RedeemedReward[];
    } catch (error) {
      console.error('Error getting redeemed rewards by status:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Get reward statistics
  async getRewardStats(): Promise<{
    totalRedeemed: number;
    totalUsed: number;
    totalSpent: number;
    averageCost: number;
    mostPopularCategory: string;
  }> {
    try {
      const history = await this.getRewardHistory();
      
      const averageCost = history.totalRedeemed > 0 
        ? Math.round(history.totalSpent / history.totalRedeemed) 
        : 0;

      // Find most popular category
      const categoryCounts: { [key: string]: number } = {};
      history.rewards.forEach(reward => {
        categoryCounts[reward.category] = (categoryCounts[reward.category] || 0) + 1;
      });

      const mostPopularCategory = Object.keys(categoryCounts).reduce((a, b) => 
        categoryCounts[a] > categoryCounts[b] ? a : b, 'None'
      );

      return {
        totalRedeemed: history.totalRedeemed,
        totalUsed: history.totalUsed,
        totalSpent: history.totalSpent,
        averageCost,
        mostPopularCategory
      };

    } catch (error) {
      console.error('Error getting reward stats:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Propose a new reward (user-initiated, no cost, pending approval)
  async proposeReward(name: string, description: string, category: string, imageUrl?: string): Promise<void> {
    try {
      const userId = getCurrentUserId();
      const reward = {
        name,
        description,
        category,
        imageUrl: imageUrl || '',
        available: false,
        createdAt: new Date(),
        updatedAt: new Date(),
        status: 'pending_approval',
        requestedBy: userId
      };
      await addDoc(collection(db, COLLECTIONS.REWARDS), {
        ...reward,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error proposing reward:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Fetch all pending reward proposals (for approvers)
  async getPendingRewardProposals(): Promise<Reward[]> {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Please sign in to continue.');
      
      // Check if user is an approver by looking for credit_approvers records
      try {
        const approverQuery = query(
          collection(db, COLLECTIONS.CREDIT_APPROVERS),
          where('approverEmail', '==', user.email?.toLowerCase()),
          where('status', '==', 'accepted')
        );
        const approverSnapshot = await getDocs(approverQuery);
        
        // If user is not an approver, return empty array (only approvers should see all pending proposals)
        if (approverSnapshot.empty) {
          return [];
        }
      } catch (error) {
        // If there's an error checking approver status (like permission issues), assume user is not an approver
        console.log('Error checking approver status, assuming user is not an approver:', error);
        return [];
      }
      
      // User is an approver, proceed with fetching all pending proposals
      const q = query(
        collection(db, COLLECTIONS.REWARDS),
        where('status', '==', 'pending_approval')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          description: data.description,
          cost: data.cost,
          category: data.category,
          imageUrl: data.imageUrl,
          available: data.available,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          status: data.status,
          requestedBy: data.requestedBy,
          rejectionReason: data.rejectionReason
        };
      });
    } catch (error) {
      console.error('Error fetching pending reward proposals:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Fetch user's own reward proposals (for users to see their proposals)
  async getUserRewardProposals(): Promise<Reward[]> {
    try {
      const userId = getCurrentUserId();
      
      const q = query(
        collection(db, COLLECTIONS.REWARDS),
        where('requestedBy', '==', userId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          description: data.description,
          cost: data.cost,
          category: data.category,
          imageUrl: data.imageUrl,
          available: data.available,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          status: data.status,
          requestedBy: data.requestedBy,
          rejectionReason: data.rejectionReason
        };
      });
    } catch (error) {
      console.error('Error fetching user reward proposals:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Update a reward proposal (by id)
  async updateRewardProposal(rewardId: string, updates: Partial<Reward>): Promise<void> {
    try {
      const rewardRef = doc(db, COLLECTIONS.REWARDS, rewardId);
      await updateDoc(rewardRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating reward proposal:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Fetch approved rewards that the current user proposed
  async getApprovedRewards(): Promise<Reward[]> {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Please sign in to continue.');
      
      const q = query(
        collection(db, COLLECTIONS.REWARDS),
        where('status', '==', 'approved'),
        where('available', '==', true),
        where('requestedBy', '==', user.uid)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          description: data.description,
          cost: data.cost,
          category: data.category,
          imageUrl: data.imageUrl,
          available: data.available,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          status: data.status,
          requestedBy: data.requestedBy,
          rejectionReason: data.rejectionReason
        };
      });
    } catch (error) {
      console.error('Error fetching approved rewards:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Send a notification to a user about their proposal status
  async sendProposalNotification(userId: string, type: 'approved' | 'rejected', rewardName: string): Promise<void> {
    try {
      await addDoc(collection(db, 'notifications'), {
        userId,
        type,
        rewardName,
        createdAt: serverTimestamp(),
        read: false
      });
    } catch (error) {
      console.error('Error sending proposal notification:', error);
    }
  },

  // Fetch all redeemed rewards for the current user
  async getMyRedeemedRewards(): Promise<RedeemedReward[]> {
    try {
      const userId = getCurrentUserId();
      const q = query(
        collection(db, COLLECTIONS.REDEEMED_REWARDS),
        where('userId', '==', userId),
        orderBy('redeemedAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          userId: data.userId,
          rewardId: data.rewardId,
          rewardName: data.rewardName,
          rewardDescription: data.rewardDescription,
          cost: data.cost,
          category: data.category,
          redeemedAt: data.redeemedAt?.toDate() || new Date(),
          usedAt: data.usedAt?.toDate(),
          status: data.status,
          notes: data.notes,
          approvalRequestedAt: data.approvalRequestedAt?.toDate(),
          approvalNotes: data.approvalNotes,
          approvalRequestedBy: data.approvalRequestedBy,
          approvedBy: data.approvedBy,
          approvedAt: data.approvedAt?.toDate(),
          approverNotes: data.approverNotes,
          rejectedBy: data.rejectedBy,
          rejectedAt: data.rejectedAt?.toDate(),
          rejectionReason: data.rejectionReason
        };
      });
    } catch (error) {
      console.error('Error fetching redeemed rewards:', error);
      throw new Error(getErrorMessage(error));
    }
  }
}; 